import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { supabase, refreshAuthStorage } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { setRememberMePreference, getRememberMePreference, clearAllAuthStorage } from "@/utils/authStorage";
import { authRateLimiter, getSecureErrorMessage } from "@/utils/securityUtils";
import SecurityAuditService from "@/services/securityAuditService";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import type { Session, User } from "@supabase/supabase-js";
import { getCurrentSessionId } from "@/services/sessionService";
import { twoFactorService } from "@/services/twoFactorService";

// Utility function to detect mobile devices
const isMobileDevice = () => {
  return window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const useBuyerAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [pending2FA, setPending2FA] = useState(false);
  const [pending2FAUserId, setPending2FAUserId] = useState<string | null>(null);
  const [pending2FAMethod, setPending2FAMethod] = useState<'totp' | 'sms' | null>(null);
  const [tempSessionData, setTempSessionData] = useState<{ session: Session; user: User; profile: any } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const signInAsBuyer = async (email: string, password: string, rememberMe: boolean = false) => {
    // Create timeout promise to prevent indefinite hanging (e.g., in Chrome Custom Tabs)
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Sign in is taking too long. Please try again.'));
      }, 30000); // 30 second timeout
    });

    const performSignIn = async () => {
      // Check rate limiting
      if (authRateLimiter.isRateLimited(email)) {
        throw new Error('Too many attempts. Please wait before trying again.');
      }

      // Record the attempt
      authRateLimiter.recordAttempt(email);

      console.log("Starting buyer sign in for:", email);
      logger.debug("Starting buyer sign in process", { isMobile: isMobileDevice(), rememberMe });
      
      // Set remember me preference BEFORE authentication (synchronously)
      setRememberMePreference(rememberMe, 'buyer');
      logger.debug("Remember me preference set", { rememberMe });
      
      // Sign in with Supabase auth
      console.log("Attempting Supabase authentication...");
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) {
        console.error("Supabase auth error:", error);
        logger.error("Buyer sign in error", { error: error.message });
        throw error;
      }
      
      // Refresh storage to ensure session is in correct storage type
      await refreshAuthStorage();
      logger.debug("Auth storage refreshed based on remember me preference");

      console.log("Supabase auth successful for user:", data.user?.id);
      logger.debug("Buyer sign in successful, checking 2FA and profile");

      if (data.user) {
        // Check if user has 2FA enabled BEFORE completing sign-in
        const { data: user2FA, error: twoFAError } = await supabase
          .from('user_2fa')
          .select('enabled, method')
          .eq('user_id', data.user.id)
          .maybeSingle() as { data: { enabled: boolean; method: string } | null; error: any };

        if (!twoFAError && user2FA?.enabled) {
          // Check if current device is trusted
          const deviceId = getCurrentSessionId();
          const isTrusted = await twoFactorService.isDeviceTrusted(data.user.id, deviceId);

          if (isTrusted) {
            console.log("Device is trusted, skipping 2FA");
            logger.debug("Trusted device detected, bypassing 2FA");
            // Continue with normal sign-in flow (existing code below will handle it)
          } else {
            // Device not trusted, require 2FA
            console.log("User has 2FA enabled, requiring verification");
            logger.debug("2FA enabled for user, deferring sign-in completion", { method: user2FA.method });
            
            const method = user2FA.method as 'totp' | 'sms';
            
            // Auto-trigger SMS for SMS users
            if (method === 'sms') {
              try {
                await supabase.functions.invoke('send-2fa-sms', {
                  body: { user_id: data.user.id }
                });
                console.log("SMS code sent automatically");
              } catch (smsError) {
                console.error("Failed to send SMS code:", smsError);
              }
            }
            
            // Don't complete sign-in yet - wait for 2FA verification
            setPending2FA(true);
            setPending2FAUserId(data.user.id);
            setPending2FAMethod(method);
            setTempSessionData({ 
              session: data.session, 
              user: data.user,
              profile: null // Will be fetched after 2FA
            });
            return; // Exit early, don't navigate
          }
        }

        console.log("No 2FA or 2FA disabled, continuing with normal sign-in");
        
        // Check if user has a buyer profile
        console.log("Checking for buyer profile...");
        const { data: buyerProfile, error: profileError } = await supabase
          .from("buyer_profiles")
          .select("id, subscription_status, full_name")
          .eq("user_id", data.user.id)
          .maybeSingle();

        console.log("Buyer profile query result:", { buyerProfile, profileError });

        if (profileError && profileError.code !== 'PGRST116') {
          console.error("Error checking buyer profile:", profileError);
          logger.error("Error checking buyer profile", { error: profileError.message });
          
          // Sign out and show error
          await supabase.auth.signOut();
          toast({
            title: "Account Issue",
            description: "We couldn't verify your account. Please try signing in again or contact support.",
            variant: "destructive",
          });
          return;
        }

        if (buyerProfile) {
          console.log("Buyer profile found:", buyerProfile);
          logger.debug("Buyer profile found, navigating to intended destination");
          
          // Reset rate limiting on successful login
          authRateLimiter.reset(email);
          
          // Set Sentry user context for error tracking
          setSentryUser({ id: data.user.id, email: data.user.email }, 'buyer');
          
          // Update last login timestamp
          await supabase.rpc('update_last_login');
          
          // Log successful login
          await SecurityAuditService.logLoginSuccess({ user_type: 'buyer' });
          
          // Navigate to intended destination or fall back to dashboard
          const intendedDestination = location.state?.from?.pathname || "/buyer/dashboard";
          navigate(intendedDestination, { replace: true });
          toast({
            title: "Welcome back!",
            description: `Signed in successfully as ${buyerProfile.full_name || 'buyer'}`,
          });
        } else {
          console.log("No buyer profile found for user:", data.user.id);
          logger.warn("No buyer profile found, signing out");
          // User exists but is not a buyer, sign them out and show error
          await supabase.auth.signOut();
          toast({
            title: "Access denied",
            description: "This account is not registered as a buyer. Please use the correct login page or create a buyer account.",
            variant: "destructive",
          });
        }
      }
    };

    try {
      setIsLoading(true);
      
      // Race sign-in against timeout to prevent indefinite hanging
      await Promise.race([performSignIn(), timeoutPromise]);
    } catch (error: any) {
      console.error("Unexpected error during buyer sign in:", error);
      logger.error("Buyer sign in failed", { error: error.message });
      
      // On timeout, clear potentially corrupted auth state
      if (error.message && error.message.includes('taking too long')) {
        console.log('Sign in timeout - clearing auth storage');
        clearAllAuthStorage();
        await supabase.auth.signOut().catch(() => {});
      }
      
      // Log failed login attempt
      await SecurityAuditService.logLoginFailure(error.message, { user_type: 'buyer' });
      
      // Use secure error messaging
      const secureMessage = getSecureErrorMessage(error);
      
      // Re-throw with secure message so BuyerLoginPage can handle it
      throw new Error(secureMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const complete2FAVerification = async () => {
    if (!tempSessionData) {
      logger.error("No temp session data available for 2FA completion");
      return;
    }

    try {
      console.log("Completing buyer sign-in after 2FA verification");
      logger.debug("2FA verified, completing buyer sign-in process");
      
      const { user } = tempSessionData;
      
      // Fetch buyer profile
      const { data: buyerProfile } = await supabase
        .from("buyer_profiles")
        .select("id, subscription_status, full_name")
        .eq("user_id", user.id)
        .maybeSingle();

      if (buyerProfile) {
        // Reset rate limiting on successful login
        authRateLimiter.reset(user.email || '');
        
        // Update last login timestamp
        await supabase.rpc('update_last_login');
        
        // Log successful login
        await SecurityAuditService.logLoginSuccess({ user_type: 'buyer' });
        
        // Determine navigation based on subscription status
        const from = location.state?.from?.pathname || "/buyer/dashboard";
        navigate(from, { replace: true });
        
        toast({
          title: "Welcome back!",
          description: `Signed in successfully as ${buyerProfile.full_name || 'buyer'}`,
        });
      }
      
      // Clear pending 2FA state
      setPending2FA(false);
      setPending2FAUserId(null);
      setTempSessionData(null);
    } catch (error: any) {
      logger.error("Error completing buyer 2FA verification", { error: error.message });
      toast({
        title: "Error",
        description: "Failed to complete sign-in. Please try again.",
        variant: "destructive",
      });
    }
  };

  const signOutAsBuyer = async () => {
    try {
      logger.debug("Starting buyer sign out process");
      setIsLoading(true);
      
      // Clear Sentry user context
      clearSentryUser();
      
      // Check if running in Lovable preview
      const isPreview = typeof window !== 'undefined' && 
                       new URLSearchParams(window.location.search).has('__lovable_token');
      
      // Get remember me preference before clearing
      const rememberMe = getRememberMePreference();
      
      // Perform Supabase logout
      await supabase.auth.signOut();
      
      // Clear pending 2FA state
      setPending2FA(false);
      setPending2FAUserId(null);
      setTempSessionData(null);
      
      // For Lovable preview OR when remember me was disabled, do aggressive cleanup
      if (isPreview || !rememberMe) {
        logger.debug("Performing deep cleanup for buyer (preview or remember me disabled)");
        clearAllAuthStorage();
        
        // Use direct navigation for preview to avoid parameter restoration
        if (isPreview) {
          toast({
            title: "Signed out",
            description: "You have been successfully signed out.",
          });
          
          // Force reload to clean URL without __lovable_token
          window.location.href = window.location.origin + '/buyer/login';
          return;
        }
      }
      
      // Normal navigation for production
      navigate("/buyer/login", { replace: true });
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
    } catch (error: any) {
      logger.error("Buyer sign out failed", { error: error.message });
      
      // Force cleanup even on error
      clearAllAuthStorage();
      
      toast({
        title: "Sign out failed",
        description: error.message || "An error occurred during sign out.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signInAsBuyer,
    signOutAsBuyer,
    complete2FAVerification,
    isLoading,
    pending2FA,
    pending2FAUserId,
    pending2FAMethod
  };
};
